import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Container, CheckTree, IconButton, Tooltip, Whisper } from 'rsuite'
import CloseIcon from '@rsuite/icons/Close'
import HourglassIcon from '@rsuite/icons/legacy/Hourglass'
import SpinnerIcon from '@rsuite/icons/legacy/Spinner'
import CheckRoundIcon from '@rsuite/icons/CheckRound'
import WarningRoundIcon from '@rsuite/icons/WarningRound'
import CloseOutlineIcon from '@rsuite/icons/CloseOutline'
import useQuery from '@/client/utils/useQuery'
import ConverterListModel from '@/client/models/converter'
import InTableColumnModel from '@/client/models/inTableColumn'
import { PanelError } from '@/client/components/ErrorProvider'
import { Button } from '@/client/components/Controls'
import Loading from '@/client/components/Loading'
import { MessageBox } from '@/client/components/MessageBox'
import toastMessage, { toastError } from '@/client/utils/toastMessage'
import project from '@client/stores/projectStore'
import { user } from '@client/stores/authStore'
import { TaskView } from '@client/stores/progressStore'
import { refreshProgress } from '@client/stores/progressWatch'
import { useLayout } from '@client/components/Layout/LayoutManager.tsx'
import ConverterIssues from '@client/views/ConverterIssues.tsx'
import { isActive, mayCancel, ownActive, rowKey } from './convertQueue'
import useConvertQueue from './useConvertQueue'

//
// The converters of a project and the files of their data lists, to convert.
//
// A start puts the chosen files into the project's queue on the server
// (services/convert/queue), which works them off one after the other. There
// is no dialog: each row shows how its file stands — waiting, running, how it
// ended — for everyone who has the project open (operator, 17.10.2026).
//

type ConverterProps = { tab: any }

const converterListModel = new ConverterListModel()
const inTableColumnModel = new InTableColumnModel()

/** A leaf's value carries the file; the server wants it in pieces. */
const fileOf = (value: string) => {
    const [converterId, inTableId, fileId, fileName, sheetName, client] = value.split('|')
    return { converterId, inTableId, fileId, fileName, sheetName, client }
}

const rowKeyOfValue = (value: string) => {
    const { converterId, inTableId, fileId } = fileOf(value)
    return rowKey(converterId, inTableId, fileId)
}

export default function Converter({ tab }: ConverterProps) {
    const { t } = useTranslation()
    const projectId = project.currentProject.value?._id || ''
    const me = user.value
    const [refreshCounter, setRefreshCounter] = useState(0)
    const [selected, setSelected] = useState<string[]>(() => tab?.store?.treeNodes?.selected ?? [])
    const [starting, setStarting] = useState(false)
    // Asked before a start and before cancelling: a click by mistake is taken
    // back at once (operator, 18.10.2026, in place of the old countdown).
    const [question, setQuestion] = useState<{ message: string; onYes: () => void } | null>(null)
    const queue = useConvertQueue(projectId)

    const { data, loading, error } = useQuery(converterListModel.list, {
        args: {
            project: projectId,
            page: 1,
            count: 200,
            query: { intables: true },
        },
        allowEmptyVariables: false,
        refreshCounter,
    })
    const { addTab } = useLayout()

    const issuesController = {
        getTabContent({ tab }: any) {
            return <ConverterIssues tab={tab} />
        },

        getBreadCrumb(params: any) {
            const { tab } = params
            const { label } = tab.store || {}
            return [t('Converters'), t('Issues'), label || '']
        },
        useProjectInBreadCrumb() {
            return true
        },
    }

    const treeNodes = useMemo(() => {
        if (!data) return tab?.store?.treeNodes || { items: [], selected: [] }

        const selected: any[] = []
        const items: any[] = []

        data.forEach((item: any) => {
            // top-level (group) node

            const node: any = { label: `${item.name} (${item.outTableId})`, value: item._id, children: [] }
            item.inTables?.forEach((inTable: any) => {
                inTable.files?.forEach((file: any) => {
                    const { warnings = 0, errors = 0 } = item.issuesByFile[file._id] || { warnings: 0, errors: 0 }

                    const value = `${item._id}|${inTable._id}|${file._id}|${file.fileName}|${file.sheetName}|${file.client}`
                    node.children.push({
                        converterId: item._id,
                        inTableId: inTable._id,
                        fileId: file._id,
                        label: `${file.fileName} - ${file.sheetName}`,
                        value,
                        parent: node,
                        warnings,
                        errors,
                        outTableId: item.outTableId,
                    } as never)

                    if (file.useOnMapping) selected.push(value)
                })
            })

            items.push(node as never)
        })

        tab.store.treeNodes = { items, selected }
        return tab.store.treeNodes
    }, [data])

    // what the server has stored as chosen, once the list is read
    useEffect(() => {
        if (data) setSelected(treeNodes.selected)
    }, [treeNodes])

    const taskOfValue = (value: string): TaskView | undefined => queue[rowKeyOfValue(value)]

    // Files that wait or run have no checkbox: they can not be chosen, do not
    // count for "all", and a choice they had is dropped (Nachträge 3 und 4).
    const lockedValues = useMemo(
        () =>
            (treeNodes?.items ?? []).flatMap((node: any) =>
                node.children.filter((leaf: any) => isActive(taskOfValue(leaf.value))).map((leaf: any) => leaf.value)
            ),
        [treeNodes, queue]
    )
    // A file that waits or runs is left out of the start, but keeps its
    // choice: once it is free it is ticked again, as it is stored (operator,
    // 18.10.2026: "nach dem Konvertieren soll die Auswahl bestehen bleiben").
    const chosen = useMemo(() => selected.filter((value) => !lockedValues.includes(value)), [selected, lockedValues])
    const mine = ownActive(queue, me)

    // The counts of warnings and errors change with every file converted. Read
    // once the queue has run dry rather than after each file.
    const anyActive = Object.values(queue).some((task) => isActive(task))
    const wasActive = useRef(anyActive)
    useEffect(() => {
        if (wasActive.current && !anyActive) setRefreshCounter((n) => n + 1)
        wasActive.current = anyActive
    }, [anyActive])

    if (error) return <PanelError message={error.message} />
    if (loading) return <Loading />

    const choose = (next: string[]) => {
        tab.store.treeNodes.selected = next
        setSelected(next)
    }

    // on click save checkbox state immediately to database
    function onSelect(item: any) {
        const checked = item.check
        // a group node takes along only the children that can be chosen
        const leaves = (item.children ?? [item]).filter((leaf: any) => !lockedValues.includes(leaf.value))
        const values = leaves.map((leaf: any) => leaf.value)

        choose(checked ? [...new Set([...selected, ...values])] : selected.filter((value) => !values.includes(value)))
        inTableColumnModel.updateUseOnMapping(projectId, {
            ids: leaves.map((leaf: any) => `${leaf.inTableId}-${leaf.fileId}`),
            checked,
        })
    }

    const ask = (message: string, onYes: () => void) => setQuestion({ message, onYes })

    async function onStart() {
        setStarting(true)
        const answer = await converterListModel.convert(projectId, { files: chosen.map(fileOf) })
        setStarting(false)

        if (answer.reason) return toastError(answer.reason)

        for (const refused of answer.data?.rejected ?? []) {
            toastMessage(t('{{file}}: {{reason}}', { file: `${refused.fileName} - ${refused.sheetName}`, reason: refused.message }), 'warning')
        }

        // Said once, so that a start is never "nothing happened" — not even
        // when the rows do not get to hear of it.
        const count = answer.data?.queued?.length ?? 0
        if (count) toastMessage(t('{{count}} file(s) queued', { count }))

        // the events bring the rows; this covers a socket that is not up yet
        void refreshProgress({ projectId })
    }

    async function onCancelAll() {
        const answer = await converterListModel.cancelAllConverts(projectId)
        if (answer.reason) toastError(answer.reason)
    }

    async function onCancel(task: TaskView) {
        const answer = await converterListModel.cancelConvert(projectId, task.id)
        if (answer.reason) toastError(answer.reason)
    }

    function openIssues(nodeData: any) {
        const outTableId = nodeData.outTableId

        addTab({
            dataId: `converter-issues-${outTableId}-${nodeData.fileId}`,
            name: t('Issues'),
            component: 'converterIssues',
            controller: issuesController,
            store: { projectId, outTableId, label: nodeData.label, fileId: nodeData.fileId },
        })
    }

    /** Who started it, and who cancelled it when that was somebody else. */
    function describeTask(task: TaskView) {
        const name = task.createdByName
        switch (task.state) {
            case 'queued':
                return name ? t('Waiting · started by {{name}}', { name }) : t('Waiting')
            case 'running':
                return name
                    ? t('Converting {{percent}} % · started by {{name}}', { percent: task.progress, name })
                    : t('Converting {{percent}} %', { percent: task.progress })
            case 'finished':
                return t('Converted')
            case 'failed':
                return task.error ? t('Conversion failed: {{error}}', { error: task.error }) : t('Conversion failed')
            default:
                return task.cancelledByName ? t('Cancelled by {{name}}', { name: task.cancelledByName }) : t('Cancelled')
        }
    }

    /** Where the checkbox would be: what the file is doing, in the same width. */
    function renderQueueSymbol(task: TaskView) {
        return (
            <Whisper placement="top" trigger="hover" speaker={<Tooltip>{describeTask(task)}</Tooltip>}>
                <span className={`convert-queue-symbol convert-queue-symbol--${task.state}`} data-state={task.state} data-task={task.id}>
                    {task.state === 'queued' ? <HourglassIcon /> : <SpinnerIcon spin />}
                </span>
            </Whisper>
        )
    }

    /** Behind the name: how the file ended, for as long as the server keeps it. */
    function renderQueueMark(task: TaskView) {
        const Icon = task.state === 'finished' ? CheckRoundIcon : task.state === 'failed' ? WarningRoundIcon : CloseOutlineIcon
        return (
            <Whisper placement="top" trigger="hover" speaker={<Tooltip>{describeTask(task)}</Tooltip>}>
                <span className={`convert-queue-mark convert-queue-mark--${task.state}`} data-state={task.state} data-task={task.id}>
                    <Icon />
                </span>
            </Whisper>
        )
    }

    /**
     * The running file (operator, 18.10.2026, variant B): the percentage at
     * the right in a fixed width, the ⨯ beside it, and a thin bar under the
     * whole row.
     */
    function renderRunning(task: TaskView, onCancelClick: React.MouseEventHandler<HTMLElement>) {
        return (
            <>
                <span className="convert-queue-running">
                    <span className="convert-queue-percent">{t('{{percent}} %', { percent: task.progress })}</span>
                    {mayCancel(task, me) && (
                        <Whisper placement="top" trigger="hover" speaker={<Tooltip>{t('Cancel')}</Tooltip>}>
                            <IconButton
                                appearance="subtle"
                                size="xs"
                                className="convert-queue-cancel"
                                aria-label={t('Cancel')}
                                icon={<CloseIcon />}
                                onClick={onCancelClick}
                            />
                        </Whisper>
                    )}
                </span>
                <span className="convert-queue-bar" aria-hidden="true">
                    <span className="convert-queue-bar-value" style={{ width: `${task.progress}%` }} />
                </span>
            </>
        )
    }

    function renderTreeNode(nodeData: any) {
        const isLeaf = !nodeData?.children || nodeData.children.length === 0
        const warn = isLeaf ? (nodeData.warnings ?? 0) : 0
        const err = isLeaf ? (nodeData.errors ?? 0) : 0
        const show = isLeaf && (warn > 0 || err > 0)
        const task = isLeaf ? taskOfValue(nodeData.value) : undefined
        const active = isActive(task)

        const onBadgeClick: React.MouseEventHandler<HTMLSpanElement> = (e) => {
            e.preventDefault()
            e.stopPropagation()
            if (isLeaf) openIssues(nodeData)
        }

        const onCancelClick: React.MouseEventHandler<HTMLElement> = (e) => {
            e.preventDefault()
            e.stopPropagation()
            if (task) ask(t('Cancel the conversion?'), () => void onCancel(task))
        }

        return (
            <div className="treenode-item" data-row={isLeaf ? rowKeyOfValue(nodeData.value) : undefined}>
                {active && renderQueueSymbol(task!)}
                <span className="treenode-label">
                    {nodeData.label}
                    {task && !active && renderQueueMark(task)}
                    {/* a waiting file keeps the link; how it looks is not decided yet */}
                    {task?.state === 'queued' && mayCancel(task, me) && (
                        <Button appearance="link" size="xs" className="convert-queue-cancel" onClick={onCancelClick}>
                            {t('Cancel')}
                        </Button>
                    )}
                    {show && (
                        <span
                            onClick={onBadgeClick}
                            title={t('Open converter issues')}
                            style={{
                                marginLeft: 8,
                                display: 'inline-flex',
                                gap: 6,
                                alignItems: 'center',
                                cursor: 'pointer',
                            }}
                            className="badge-group"
                        >
                            {warn > 0 && (
                                <span title={t('Warnings')} className="badge badge--warn">
                                    {warn}
                                </span>
                            )}
                            {err > 0 && (
                                <span title={t('Errors')} className="badge badge--error">
                                    {err}
                                </span>
                            )}
                        </span>
                    )}
                </span>
                {task?.state === 'running' && renderRunning(task, onCancelClick)}
            </div>
        )
    }

    const ConverterTop = () => {
        return (
            <Container className="mapping-converter-top">
                <span>{/* dummy */}</span>
                {mine.length ? (
                    // Only one's own: those of others stay, and their rows say whose they are.
                    <Button appearance="ghost" onClick={() => ask(t('Cancel the conversion?'), onCancelAll)}>
                        {t('Cancel all')}
                    </Button>
                ) : (
                    <Button appearance="ghost" disabled={!chosen.length || starting} onClick={() => ask(t('Start the conversion?'), onStart)}>
                        {t('Start mapping')}
                    </Button>
                )}
            </Container>
        )
    }

    return (
        <Container className="mapping-converter">
            {question && (
                <MessageBox
                    message={question.message}
                    buttons={[
                        { key: 'yes', text: t('Yes') },
                        { key: 'no', text: t('No') },
                    ]}
                    onClick={(key) => {
                        const { onYes } = question
                        setQuestion(null)
                        if (key === 'yes') onYes()
                    }}
                />
            )}
            <ConverterTop />
            <Container className="mapping-converter-content">
                <CheckTree
                    defaultExpandAll
                    value={chosen}
                    data={treeNodes?.items}
                    uncheckableItemValues={lockedValues}
                    onSelect={onSelect}
                    renderTreeNode={renderTreeNode}
                />
            </Container>
        </Container>
    )
}
